<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { formatDateTime, formatNumber } from '../../utils/format'
import { useRestData } from '../../composables/useRestData'
import { useApi } from '../../composables/api'
import CrudDataTable from '../../components/CrudDataTable.vue'
import FileAttentionChips from '../../components/staff/FileAttentionChips.vue'
import { fileStatus, parseFileAttention, type FileAttention } from '../../utils/file-attention'
import type { UserFileStatusCountsResponse } from '@roadfootage/api-client'

const { adminUserFilesApi } = useApi()
const $q = useQuasar()
const bulkReprocessing = ref(false)

const bulkReprocess = (selected: any[], clearSelection: () => void) => {
  $q.dialog({
    title: 'Reprocess Files',
    message: `Requeue ${selected.length} file${selected.length !== 1 ? 's' : ''} for media processing?`,
    cancel: true,
    ok: { label: 'Reprocess', color: 'primary' }
  }).onOk(async () => {
    bulkReprocessing.value = true
    const results = await Promise.allSettled(
      selected.map((item) => adminUserFilesApi.adminUserFilesReprocessFileById({ id: item.id }))
    )
    const failed = results.filter((r) => r.status === 'rejected').length
    bulkReprocessing.value = false
    clearSelection()
    if (failed === 0) {
      $q.notify({
        type: 'positive',
        message: `${selected.length} file${selected.length !== 1 ? 's' : ''} queued for reprocessing.`
      })
    } else {
      $q.notify({
        type: 'warning',
        message: `${selected.length - failed} queued, ${failed} failed.`
      })
    }
  })
}
const route = useRoute()
const router = useRouter()

// The quick filter lives in the URL (?attention=Threat) so the dashboard can link to it (RF-131).
const attention = ref<FileAttention>(parseFileAttention(route.query))
const counts = ref<UserFileStatusCountsResponse | null>(null)

// 1. Setup the headless data fetcher
const data = useRestData(
  (params, init) =>
    adminUserFilesApi.adminUserFilesListByAttention(
      { ...params, attention: attention.value ?? undefined },
      init
    ),
  {
    initialPageSize: 25,
    defaultSort: [{ sortBy: 'createdAt', descending: true }]
  }
)

const loadCounts = async () => {
  try {
    counts.value = await adminUserFilesApi.adminUserFilesGetStatusCounts({})
  } catch {
    counts.value = null // the chips still filter; they just show no number
  }
}

const selectAttention = (value: FileAttention) => {
  if (value === attention.value) return
  attention.value = value
  const query = { ...route.query }
  delete query.attention
  router.replace({ query: value ? { ...query, attention: value } : query })
  data.refresh()
}

// Back/forward, or a link to this page while it is already open.
watch(
  () => route.query.attention,
  () => {
    const next = parseFileAttention(route.query)
    if (next === attention.value) return
    attention.value = next
    data.refresh()
  }
)

const rowClass = (row: any) => {
  const kind = fileStatus(row).kind
  return kind === 'threat' || kind === 'failed' ? `file-row-${kind}` : ''
}

if (route.query.id) {
  data.activeFilters.value['id'] = route.query.id as string
}

// Initial fetch
data.fetch()
loadCounts()

// 2. Define the columns based on the UserFile type definition
const columns = [
  {
    name: 'uploadFileName',
    field: 'uploadFileName',
    label: 'File Name',
    align: 'left',
    sortable: true,
    format: (val: string) => val || ''
  },
  {
    name: 'status',
    field: 'processingStatus',
    label: 'Status',
    align: 'left',
    filterable: false, // derived from two columns; staff use the quick filters instead
    format: (_val: unknown, row: any) => fileStatus(row).label
  },
  {
    name: 'uploadMimeType',
    field: 'uploadMimeType',
    label: 'MIME Type',
    align: 'left',
    sortable: true,
    format: (val: string) => val || ''
  },
  {
    name: 'uploadFileSize',
    field: 'uploadFileSize',
    label: 'Size (bytes)',
    align: 'right',
    sortable: true,
    format: (val: number) => (val ? formatNumber(val) : '')
  },
  {
    name: 'isUploaded',
    field: 'isUploaded',
    label: 'Uploaded',
    align: 'left',
    sortable: true,
    format: (val: boolean) => (val ? 'Yes' : 'No')
  },
  {
    name: 'createdAt',
    field: 'createdAt',
    label: 'Created',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? formatDateTime(val) : '')
  },
  {
    name: 'userProfileId',
    field: 'userProfileId',
    label: 'User Profile ID',
    align: 'left',
    sortable: true,
    format: (val: string) => val || ''
  },
  { name: 'actions', field: 'actions', label: 'Actions', align: 'right' }
]
</script>

<template>
  <q-page>
    <div class="q-pa-md q-pb-none">
      <FileAttentionChips
        :model-value="attention"
        :counts="counts"
        @update:model-value="selectAttention"
      />
    </div>
    <CrudDataTable
      title="Files"
      :columns="columns"
      :rest-data="data"
      :update-api="(params) => adminUserFilesApi.adminUserFilesUpdateById(params)"
      :delete-api="(params) => adminUserFilesApi.adminUserFilesDeleteById(params)"
      :row-class="rowClass"
      enable-filters
      enable-multi-sort
      enable-search
      enable-selection
    >
      <template #bulk-actions="{ selected, clearSelection }">
        <q-btn
          flat
          dense
          icon="mdi-refresh"
          label="Reprocess"
          color="primary"
          :loading="bulkReprocessing"
          @click="bulkReprocess(selected, clearSelection)"
        />
      </template>

      <template #update-form="{ item }">
        <div class="row q-col-gutter-md">
          <div class="col-12 col-md-6">
            <q-input v-model="item.uploadFileName" label="File Name" outlined readonly />
          </div>
          <div class="col-12 col-md-6">
            <q-input v-model="item.uploadMimeType" label="MIME Type" outlined readonly />
          </div>
          <div class="col-12 col-md-6">
            <q-input
              v-model="item.uploadFileSize"
              label="File Size (bytes)"
              type="number"
              outlined
              readonly
            />
          </div>
          <div class="col-12 col-md-6">
            <q-toggle v-model="item.isUploaded" label="Uploaded" disable />
          </div>
          <div class="col-12 col-md-6">
            <q-input v-model="item.userProfileId" label="User Profile ID" outlined readonly />
          </div>
          <div class="col-12 col-md-6">
            <q-input v-model="item.createdAt" label="Created At" outlined readonly />
          </div>
        </div>
      </template>
    </CrudDataTable>
  </q-page>
</template>

<style scoped>
:deep(.file-row-threat) {
  background: var(--q-negative-soft, #fdecea);
  box-shadow: inset 4px 0 0 var(--q-negative);
}
:deep(.file-row-failed) {
  background: var(--q-warning-soft, #fff6e0);
  box-shadow: inset 4px 0 0 var(--q-warning);
}
</style>
